import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import { DropdownService, FeedService } from "@/services/APIs";
import { getUserInfo } from "@/lib/storeManager";
import { CustomImageUpload } from "@/components/CustomImageUpload";
import {
  Loader2,
  Plus,
  Building2,
  Users,
  Shield,
  ThumbsUp,
  MessageSquare,
  CheckCircle,
  ChevronDown,
  Calendar,
  User,
} from "lucide-react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

type FeedScope = "my_city" | "my_region" | "1" | "2" | "3";
type FeedProvenance = "internal" | "peer" | "official";

// Sample feed data for preview
const SAMPLE_FEEDS = [
  {
    id: 1,
    type: "internal" as const,
    scope: "my_city" as const,
    provenance: "internal" as const,
    author: "Sarah Johnson",
    initials: "SJ",
    role: "Organization Organizer",
    department: "Organization Development",
    time: "2 hours ago",
    location: "San Francisco",
    daysLeft: 22,
    title: "Emergency Protocol Review",
    content:
      "Completed review of emergency protocols for upcoming winter season. Shared updated SOPs with all department heads. Coordination meeting scheduled for next week.",
    helpful: 45,
    discussions: 3,
  },
  {
    id: 2,
    type: "collaborative" as const,
    scope: "my_region" as const,
    provenance: "peer" as const,
    author: "Michael Chen",
    initials: "MC",
    role: "Public Works Director",
    department: "Infrastructure",
    time: "4 hours ago",
    location: "Oakland",
    daysLeft: 8,
    title: "Green Infrastructure Guide",
    content:
      "New SOP Available: Green Infrastructure Maintenance Guide. This document outlines sustainable drainage system maintenance procedures implemented successfully in Oakland. Available for peer review.",
    helpful: 89,
    discussions: 5,
    hasSopBadge: true,
  },
  {
    id: 4,
    type: "internal" as const,
    scope: "my_city" as const,
    provenance: "internal" as const,
    author: "David Park",
    initials: "DP",
    role: "Fire Chief",
    department: "Fire Department",
    time: "1 day ago",
    location: "San Francisco",
    daysLeft: 28,
    title: "Training Schedule Update",
    content:
      "Updated training schedule for Q1 2025 now available. All personnel please review and confirm attendance for mandatory safety drills.",
    helpful: 32,
    discussions: 7,
  },
  {
    id: 5,
    type: "collaborative" as const,
    scope: "my_region" as const,
    provenance: "peer" as const,
    author: "Lisa Martinez",
    initials: "LM",
    role: "City Manager",
    department: "Administration",
    time: "2 days ago",
    location: "San Jose",
    daysLeft: 15,
    title: "Regional Collaboration Initiative",
    content:
      "Proposing quarterly cross-city coordination meetings to share best practices in municipal services. Interested parties please respond by Friday.",
    helpful: 156,
    discussions: 12,
  },
  {
    id: 6,
    type: "regulatory" as const,
    scope: "global" as const,
    provenance: "official" as const,
    author: "Federal Emergency Management",
    initials: "US",
    role: "Official Authority",
    department: "Emergency Services",
    time: "1 day ago",
    location: "Federal Government",
    daysLeft: 0,
    title: "FEMA Guidelines Update",
    content:
      "OFFICIAL: Updated FEMA guidelines for municipal emergency response plans. All cities must submit compliance reports by Q1 2026. Templates available in Resource Library.",
    acknowledged: 892,
    audience: "All Emergency Management Directors",
    isOfficial: true,
    isClosed: true,
    threadStatus: "closed",
  },
];

export default function FeedCreation() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeScope, setActiveScope] = useState<
    "my_city" | "my_region" | "global"
  >("my_region");
  const [likedPosts, setLikedPosts] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("likedPosts");
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error("Error loading liked posts from localStorage:", error);
    }
    return new Set();
  });
  const [likingPostId, setLikingPostId] = useState<string | null>(null);
  const [openCommentPostId, setOpenCommentPostId] = useState<string | null>(
    null
  );
  const [commentTexts, setCommentTexts] = useState<{
    [postId: string]: string;
  }>({});
  const [commentsExpanded, setCommentsExpanded] = useState<{
    [postId: string]: boolean;
  }>({});

  // Save likedPosts to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        "likedPosts",
        JSON.stringify(Array.from(likedPosts))
      );
    } catch (error) {
      console.error("Error saving liked posts to localStorage:", error);
    }
  }, [likedPosts]);

  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [scope, setScope] = useState<FeedScope | "">("");
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [showOrganizationOptions, setShowOrganizationOptions] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [showPositionOptions, setShowPositionOptions] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");
  const [showDepartmentOptions, setShowDepartmentOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bannerImage, setBannerImage] = useState<
    string | { id: string; url: string; name: string; size: number } | null
  >(null);
  const [isUploading, setIsUploading] = useState(false);
  const userInfo = getUserInfo();
  const currentUserId = String(
    userInfo?.id ?? userInfo?.user_id ?? userInfo?.pk ?? ""
  );
  const currentUserDisplayName =
    userInfo?.full_name ??
    `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`.trim();

  const isFormValid =
    title.trim() && content.trim() && scope && selectedOrganization;

  // Helper to get initials from name
  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0)?.toUpperCase() ?? "";
    const last = lastName?.charAt(0)?.toUpperCase() ?? "";
    return `${first}${last}` || "?";
  };

  // Helper to format date to relative time (e.g., "2 hours ago")
  const formatRelativeTime = (dateString: string) => {
    if (!dateString) {
      return "Recently";
    }
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffSeconds = Math.floor(diffTime / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return "just now";
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
    }
    if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    }
    if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    }
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? "month" : "months"} ago`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  };

  const getCommentDisplayName = (comment: any) => {
    const apiName =
      comment.user_full_name ??
      comment.user_first_name ??
      comment.author_full_name ??
      null;

    if (apiName) {
      return apiName;
    }

    if (
      String(comment.user ?? "") === currentUserId &&
      currentUserDisplayName
    ) {
      return currentUserDisplayName;
    }

    return "User";
  };

  const getCommentAvatarSrc = (comment: any) => {
    const raw = comment.user_profile_photo;
    if (!raw || typeof raw !== "string") {
      return null;
    }
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      return raw;
    }
    const base = import.meta.env.VITE_API_BASE_URL ?? "";
    return `${base.replace(/\/$/, "")}${raw.startsWith("/") ? "" : "/"}${raw}`;
  };

  // Fetch feeds
  const {
    data: feedsData,
    isLoading: isLoadingFeeds,
    error: feedsError,
  } = useQuery({
    queryKey: ["city-collab-feeds", activeScope],
    queryFn: async () => {
      const params = activeScope === "my_city" ? { my_city: "true" } : {};
      return await FeedService.getFeeds(params);
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Fetch organization options
  const { data: organizationOptions } = useQuery({
    queryKey: ["mm_organization"],
    queryFn: () => DropdownService.getDropdownData("mm_organization", false),
  });

  // Fetch position options
  const { data: positionOptions } = useQuery({
    queryKey: ["position"],
    queryFn: () => DropdownService.getDropdownData("position", false),
  });

  // Fetch department options
  const { data: departmentOptions } = useQuery({
    queryKey: ["department"],
    queryFn: () => DropdownService.getDropdownData("department", false),
  });

  // Mutation for posting comments
  const postCommentMutation = useMutation({
    mutationFn: async (payload: { feed: string; comment: string }) => {
      const response = await FeedService.createFeedComment(payload);
      if (!response) {
        throw new Error("Failed to post comment");
      }
      return response;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["city-collab-feeds"] });
    },
    onError: (error: any) => {
      console.error("Error posting comment:", error);
      toast({
        title: "Error posting comment",
        description:
          error?.message ?? "Failed to post comment. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation for liking/unliking posts
  const likePostMutation = useMutation({
    mutationFn: async (payload: { feed: string; reaction_type: number }) => {
      const response = await FeedService.createFeedReaction(payload);
      if (!response) {
        throw new Error("Failed to update reaction");
      }
      return response;
    },
    onMutate: async variables => {
      // Optimistically update the UI immediately
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(variables.feed)) {
          newSet.delete(variables.feed);
        } else {
          newSet.add(variables.feed);
        }
        return newSet;
      });
    },
    onSuccess: () => {
      setLikingPostId(null);
      queryClient.invalidateQueries({ queryKey: ["city-collab-feeds"] });
    },
    onError: (error: any, variables) => {
      console.error("Error liking post:", error);
      // Revert the optimistic update on error
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(variables.feed)) {
          newSet.delete(variables.feed);
        } else {
          newSet.add(variables.feed);
        }
        return newSet;
      });
      setLikingPostId(null);
    },
  });

  // Sync likedPosts from API (user_has_liked) when feeds load
  useEffect(() => {
    if (feedsData && Array.isArray(feedsData)) {
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        feedsData.forEach((feed: any) => {
          const id = String(feed.id);
          if (feed.user_has_liked === true) {
            newSet.add(id);
          } else if (feed.user_has_liked === false) {
            newSet.delete(id);
          }
        });
        return newSet;
      });
    }
  }, [feedsData]);

  // Map API feeds data to match SAMPLE_FEEDS structure
  const apiFeeds = feedsData
    ? feedsData.map((feed: any) => {
        const authorName =
          feed.author_full_name ?? feed.author_first_name ?? "User";
        const initials = getInitials(
          feed.author_first_name,
          feed.author_last_name
        );

        const isSopFeed =
          feed.feed_type_name === "SOP" || feed.feed_type_name === "sop";

        // Handle attachments: image vs file (SOP often is PDF)
        const getFirstAttachment = (att: any): string | null => {
          if (!att) {
            return null;
          }
          if (Array.isArray(att) && att.length > 0) {
            return typeof att[0] === "string" ? att[0] : null;
          }
          return typeof att === "string" ? att : null;
        };

        const toAbsoluteUrl = (att: string): string => {
          if (att.startsWith("http://") || att.startsWith("https://")) {
            return att;
          }
          const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";
          return `${baseUrl}${att}`;
        };

        const getFilename = (url: string): string => {
          try {
            const clean = url.split("?")[0].split("#")[0];
            const last = clean.split("/").pop() ?? "";
            return decodeURIComponent(last) || "attachment";
          } catch {
            return "attachment";
          }
        };

        const isImageUrl = (url: string): boolean => {
          const clean = url.split("?")[0].toLowerCase();
          return /\.(png|jpe?g|gif|webp|svg)$/.test(clean);
        };

        const attachmentRaw = getFirstAttachment(feed.attachments);
        const attachmentUrl = attachmentRaw
          ? toAbsoluteUrl(attachmentRaw)
          : null;
        const attachmentName = attachmentUrl
          ? getFilename(attachmentUrl)
          : null;
        const attachmentIsImage = attachmentUrl
          ? isImageUrl(attachmentUrl)
          : false;

        // Only show images as banner images; files should render as download row
        const imageUrl =
          attachmentUrl && attachmentIsImage ? attachmentUrl : null;

        // Determine provenance from feed data
        const provenance: FeedProvenance =
          feed.provenance === "official"
            ? "official"
            : feed.provenance === "peer"
              ? "peer"
              : "internal";

        // Determine scope from feed data
        const scope: FeedScope =
          feed.scope === "my_region" || feed.scope === "regional"
            ? "my_region"
            : "my_city";

        // Calculate days left (if thread_days_remaining exists, use it, otherwise calculate from created_on)
        let daysLeft = 30;
        if (
          feed.thread_days_remaining !== undefined &&
          feed.thread_days_remaining !== null
        ) {
          daysLeft = feed.thread_days_remaining;
        } else if (feed.created_on) {
          const createdDate = new Date(feed.created_on);
          const now = new Date();
          const diffTime = Math.abs(now.getTime() - createdDate.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          daysLeft = Math.max(0, 30 - diffDays);
        }

        return {
          id: feed.id,
          type:
            feed.feed_type_name === "regulatory"
              ? "regulatory"
              : feed.feed_type_name === "collaborative"
                ? "collaborative"
                : "internal",
          scope: scope,
          provenance: provenance,
          author: authorName,
          initials: initials,
          role: feed.title ?? feed.role ?? "",
          department: feed.visibility_label ?? feed.department ?? "",
          time: formatRelativeTime(feed.created_on),
          location: feed.city ?? "San Francisco",
          daysLeft: daysLeft,
          title: feed.title ?? "",
          content: feed.content ?? "",
          helpful: feed.like_count ?? 0,
          discussions: feed.comments_count ?? 0,
          acknowledged: feed.acknowledged_count ?? feed.like_count ?? 0,
          audience: feed.audience,
          isOfficial: provenance === "official",
          hasSopBadge: isSopFeed,
          isClosed: feed.thread_closed ?? false,
          threadStatus: feed.thread_closed ? "closed" : "active",
          imageUrl: imageUrl,
          attachmentUrl,
          attachmentName,
          attachmentIsImage,
          feedComments: feed.comments ?? [],
        };
      })
    : [];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowOrganizationOptions(false);
      setShowPositionOptions(false);
      setShowDepartmentOptions(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSubmit = async () => {
    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);
    setIsUploading(true);

    try {
      // Extract file ID from bannerImage
      let attachmentId: string | null = null;
      if (bannerImage) {
        if (typeof bannerImage === "string") {
          attachmentId = bannerImage;
        } else if (
          typeof bannerImage === "object" &&
          bannerImage !== null &&
          "id" in bannerImage
        ) {
          attachmentId = bannerImage.id;
        }
      }

      // Convert visibility scope to number
      const visibility =
        scope === "1" ? 1 : scope === "2" ? 2 : scope === "3" ? 3 : 1;

      // Prepare API payload matching user requirements
      const payload: any = {
        title: title.trim(),
        content: content.trim(),
        organization_id: selectedOrganization,
        department_id: selectedDepartment || null,
        position_id: selectedPosition || null,
        visibility: visibility,
      };

      // Add attachments if banner image is uploaded
      if (attachmentId) {
        payload.attachments = attachmentId;
      }

      console.log("Creating feed with payload:", payload);

      const response = await FeedService.createFeed(payload);
      if (response) {
        toast({
          title: "Success",
          description: "Feed created successfully!",
        });

        // Close dialog
        setIsFormOpen(false);

        // Reset form fields
        setTitle("");
        setContent("");
        setScope("");
        setSelectedOrganization("");
        setSelectedPosition("");
        setSelectedDepartment("");
        setBannerImage(null);

        // Refresh feeds list
        queryClient.invalidateQueries({ queryKey: ["city-collab-feeds"] });
      } else {
        throw new Error("Failed to create feed");
      }
    } catch (error: any) {
      console.error("Error creating feed:", error);
      toast({
        title: "Error creating feed",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  // Use API feeds if available, otherwise fallback to SAMPLE_FEEDS
  const feedsToDisplay = apiFeeds.length > 0 ? apiFeeds : SAMPLE_FEEDS;

  const filteredFeeds = feedsToDisplay.filter(feed => {
    // First filter by scope
    const scopeMatch =
      activeScope === "my_city"
        ? feed.scope === "my_city"
        : feed.scope === "my_city" || feed.scope === "my_region";

    return scopeMatch;
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Scope Toggle Bar */}
      <div className="flex flex-col items-center gap-2 sm:gap-3">
        <Tabs
          value={activeScope}
          onValueChange={v => setActiveScope(v as typeof activeScope)}
          className="w-full max-w-lg"
        >
          <TabsList className="grid h-10 w-full grid-cols-2 bg-muted/80 sm:h-11">
            <TabsTrigger
              value="my_city"
              className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm sm:gap-2 sm:text-sm"
            >
              <Building2 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="xs:inline hidden">My</span> City
            </TabsTrigger>
            <TabsTrigger
              value="my_region"
              className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm sm:gap-2 sm:text-sm"
            >
              <Users className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="xs:inline hidden">My</span> Region
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="px-2 text-center text-xs text-muted-foreground sm:text-sm">
          {activeScope === "my_city" &&
            "Showing internal updates and SOPs from your city only"}
          {activeScope === "my_region" &&
            "Showing internal posts plus updates from connected peer agencies"}
        </p>
      </div>

      {/* Header with Provenance Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          Operational Feed
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* Create Feed Button */}
          <Button
            variant="outline"
            className="border-border text-xs text-foreground sm:text-sm"
            onClick={() => setIsFormOpen(true)}
          >
            <Plus className="mr-1 h-3 w-3 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="xs:inline hidden">Create</span> Feed
          </Button>
        </div>
      </div>

      {/* Feed Cards */}
      <div className="space-y-4">
        {isLoadingFeeds ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Loading feeds...
            </CardContent>
          </Card>
        ) : feedsError ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-destructive">
              Failed to load feeds. Please try again later.
            </CardContent>
          </Card>
        ) : filteredFeeds.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-muted-foreground">
              <Shield className="mx-auto mb-3 h-12 w-12 opacity-50" />
              <p className="text-sm font-medium">No feeds available</p>
              <p className="mt-1 text-xs">Try changing the filter or scope</p>
            </div>
          </Card>
        ) : (
          filteredFeeds.map(feed => {
            const isPeer = feed.provenance === "peer";
            const isOfficial = feed.provenance === "official";

            return (
              <Card
                key={feed.id}
                className="overflow-hidden border-border bg-card text-card-foreground dark:border-border dark:bg-card dark:text-card-foreground"
                style={{ borderRadius: "16px" }}
              >
                <CardContent className="p-5">
                  {/* Header matching CitycollabFeedItem */}
                  <h3 className="mb-3 font-sans text-[18px] font-semibold leading-relaxed text-foreground">
                    {feed.title}
                  </h3>

                  <div className="mb-3 flex items-center text-sm text-muted-foreground">
                    <User size={16} className="mr-2 text-primary" />
                    <span className="mr-4 font-sans text-[14px] font-semibold">
                      {feed.author}
                    </span>
                    <Calendar size={16} className="mr-2 text-primary" />
                    <span className="font-sans text-[14px] font-semibold">
                      {feed.time}
                    </span>
                  </div>

                  {/* Meta/Tags row from reference style */}
                  <div className="mb-4 flex flex-wrap gap-2">
                    {isOfficial && (
                      <Badge
                        variant="outline"
                        className="rounded-full border-primary bg-transparent px-3 py-1 font-sans text-xs font-medium text-primary"
                      >
                        Official
                      </Badge>
                    )}
                    {isPeer && (
                      <Badge
                        variant="outline"
                        className="border-success text-success rounded-full bg-transparent px-3 py-1 font-sans text-xs font-medium"
                      >
                        Peer
                      </Badge>
                    )}
                    {!isOfficial && !isPeer && (
                      <Badge
                        variant="outline"
                        className="rounded-full border-muted-foreground bg-transparent px-3 py-1 font-sans text-xs font-medium text-muted-foreground"
                      >
                        Internal
                      </Badge>
                    )}
                    {feed.hasSopBadge && (
                      <Badge
                        variant="outline"
                        className="rounded-full border-primary bg-transparent px-3 py-1 font-sans text-xs font-medium text-primary"
                      >
                        SOP
                      </Badge>
                    )}
                    {feed.audience && isOfficial && (
                      <Badge
                        variant="outline"
                        className="rounded-full border-amber-300 bg-transparent px-3 py-1 font-sans text-xs font-medium text-amber-700 dark:border-amber-600 dark:text-amber-400"
                      >
                        Audience: {feed.audience}
                      </Badge>
                    )}
                  </div>

                  {/* Content */}
                  <p className="mb-4 leading-relaxed text-foreground dark:text-foreground">
                    {feed.content}
                  </p>

                  {/* SOP attachment (non-image file) */}
                  {(feed as any).hasSopBadge &&
                    (feed as any).attachmentUrl &&
                    !(feed as any).attachmentIsImage && (
                      <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground">
                            {(feed as any).attachmentName ?? "SOP Attachment"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Click download to open/save the file
                          </div>
                        </div>
                        <a
                          href={(feed as any).attachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          download
                          className="shrink-0 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                        >
                          Download
                        </a>
                      </div>
                    )}

                  {/* Banner Image */}
                  {(feed as any).imageUrl && (
                    <div className="mb-4 overflow-hidden rounded-lg border border-border">
                      <img
                        src={(feed as any).imageUrl}
                        alt="Feed banner"
                        className="h-48 w-full object-cover"
                      />
                    </div>
                  )}
                  {/* Fallback for SAMPLE_FEEDS */}
                  {!(feed as any).imageUrl &&
                    (feed.id === 1 || feed.id === 2 || feed.id === 5) && (
                      <div className="mb-4 overflow-hidden rounded-lg border border-border">
                        <img
                          src={
                            feed.id === 1
                              ? "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&h=400&fit=crop"
                              : feed.id === 2
                                ? "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800&h=400&fit=crop"
                                : "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&h=400&fit=crop"
                          }
                          alt="Feed banner"
                          className="h-48 w-full object-cover"
                        />
                      </div>
                    )}

                  {/* Stats with Acknowledged Count */}
                  <div className="mb-4 flex items-center gap-6 text-sm text-muted-foreground">
                    {feed.helpful !== undefined && (
                      <span className="flex items-center gap-1.5">
                        <ThumbsUp className="h-4 w-4" />
                        {feed.helpful} marked helpful
                      </span>
                    )}
                    {feed.discussions !== undefined && (
                      <span className="flex items-center gap-1.5">
                        <MessageSquare className="h-4 w-4" />
                        {feed.discussions} discussion threads
                      </span>
                    )}
                  </div>

                  {/* Comments from GET /city_collab/feeds/ nested `comments` only (no separate feed-comments list API) */}
                  {(feed as any).feedComments &&
                    (feed as any).feedComments.length > 0 && (
                      <div className="mb-4 space-y-3 border-t border-border pt-4">
                        {(() => {
                          const feedIdStr = String(feed.id);
                          const allComments = (feed as any).feedComments ?? [];
                          const isExpanded = commentsExpanded[feedIdStr];
                          const visibleComments = isExpanded
                            ? allComments
                            : allComments.slice(0, 2);
                          const hasMore = allComments.length > 2;

                          return (
                            <>
                              {visibleComments.map(
                                (comment: any, idx: number) => {
                                  const avatarSrc =
                                    getCommentAvatarSrc(comment);
                                  return (
                                    <div
                                      key={
                                        comment.id ??
                                        `${comment.user}-${comment.created_on}-${idx}`
                                      }
                                      className="flex gap-3"
                                    >
                                      {avatarSrc ? (
                                        <img
                                          src={avatarSrc}
                                          alt=""
                                          className="h-10 w-10 shrink-0 rounded-full object-cover"
                                        />
                                      ) : (
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground">
                                          {getInitials(
                                            getCommentDisplayName(comment),
                                            ""
                                          )}
                                        </div>
                                      )}
                                      <div className="flex-1 space-y-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-foreground">
                                            {getCommentDisplayName(comment)}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                            {formatRelativeTime(
                                              comment.created_on ??
                                                comment.created_at ??
                                                ""
                                            )}
                                          </span>
                                        </div>
                                        <p className="text-sm text-foreground dark:text-foreground">
                                          {comment.comment ?? ""}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                              {hasMore && (
                                <button
                                  type="button"
                                  className="text-xs text-primary hover:text-primary/90 hover:underline"
                                  onClick={() =>
                                    setCommentsExpanded(prev => ({
                                      ...prev,
                                      [String(feed.id)]: !prev[String(feed.id)],
                                    }))
                                  }
                                >
                                  {isExpanded
                                    ? "Show fewer comments"
                                    : `View more comments (${allComments.length - 2} more)`}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 border-t border-border pt-4">
                    {isOfficial ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-accent bg-accent/10 text-accent hover:bg-accent/20 dark:bg-accent/20 dark:hover:bg-accent/30"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Acknowledge âœ“
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled
                          className="text-muted-foreground"
                        >
                          Comments Disabled
                        </Button>
                        {feed.threadStatus === "active" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-600 hover:bg-amber-50 hover:text-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/30"
                          >
                            <MessageSquare className="mr-2 h-4 w-4" />
                            Policy Discussion ({feed.daysLeft} days)
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`hover:bg-primary/10 hover:text-primary ${
                            likedPosts.has(String(feed.id))
                              ? "text-primary"
                              : "text-muted-foreground"
                          }`}
                          onClick={async () => {
                            if (likingPostId === feed.id) {
                              return;
                            }
                            try {
                              setLikingPostId(feed.id);
                              await likePostMutation.mutateAsync({
                                feed: String(feed.id),
                                reaction_type: 1,
                              });
                            } catch (error) {
                              console.error("Failed to like post:", error);
                            }
                          }}
                          disabled={likingPostId === feed.id}
                        >
                          <ThumbsUp
                            className={`mr-2 h-4 w-4 ${likedPosts.has(String(feed.id)) ? "fill-primary text-primary" : ""}`}
                          />
                          {likingPostId === feed.id
                            ? "Liking..."
                            : "Mark as Helpful"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:bg-primary/10 hover:text-primary"
                          onClick={() => {
                            const feedId = String(feed.id);
                            setOpenCommentPostId(
                              openCommentPostId === feedId ? null : feedId
                            );
                          }}
                        >
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Open Discussion Thread
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Comment Input Section - Only shown when thread is opened */}
                  {openCommentPostId === String(feed.id) && (
                    <div className="space-y-2 border-t border-border pt-4">
                      <Textarea
                        placeholder="Write a comment..."
                        value={commentTexts[String(feed.id)] || ""}
                        onChange={e => {
                          setCommentTexts(prev => ({
                            ...prev,
                            [String(feed.id)]: e.target.value,
                          }));
                        }}
                        className="min-h-[80px] resize-none"
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setOpenCommentPostId(null);
                            setCommentTexts(prev => {
                              const newTexts = { ...prev };
                              delete newTexts[String(feed.id)];
                              return newTexts;
                            });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            const commentText =
                              commentTexts[String(feed.id)]?.trim();
                            if (!commentText) {
                              return;
                            }

                            try {
                              await postCommentMutation.mutateAsync({
                                feed: String(feed.id),
                                comment: commentText,
                              });
                              setCommentTexts(prev => {
                                const newTexts = { ...prev };
                                delete newTexts[String(feed.id)];
                                return newTexts;
                              });
                              setOpenCommentPostId(null);
                              toast({
                                title: "Comment posted",
                                description: "Your comment has been added.",
                              });
                            } catch (error) {
                              console.error("Failed to post comment:", error);
                            }
                          }}
                          disabled={
                            !commentTexts[String(feed.id)]?.trim() ||
                            postCommentMutation.isPending
                          }
                        >
                          {postCommentMutation.isPending
                            ? "Posting..."
                            : "Post"}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Mandatory Fair Warning Disclaimer Footer */}
      <div className="mt-6 rounded-lg border border-border bg-muted p-4 text-center">
        <p className="text-xs text-muted-foreground">
          <strong>Fair Warning:</strong> City Collab is a peer-to-peer
          discussion forum. Content shared here is not the official policy of
          CiviCircle or the participating governments unless marked as
          &quot;Official Announcement.&quot;
        </p>
      </div>

      {/* Create Feed Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Feed</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Feed Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Enter feed title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                maxLength={200}
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content">
                Feed Content <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="content"
                placeholder="Enter feed content here"
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={5}
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground">
                {content.length}/2000 characters
              </p>
            </div>

            {/* Scope */}
            <div className="space-y-2">
              <Label>
                Visibility <span className="text-destructive">*</span>
              </Label>
              <Select
                value={scope}
                onValueChange={value => setScope(value as FeedScope)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Public</SelectItem>
                  <SelectItem value="2">Organization</SelectItem>
                  <SelectItem value="3">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Organization */}
            <div className="space-y-2">
              <Label>
                Organization <span className="text-destructive">*</span>
              </Label>
              <div className="relative" onClick={e => e.stopPropagation()}>
                <div
                  onClick={e => {
                    e.stopPropagation();
                    setShowOrganizationOptions(!showOrganizationOptions);
                  }}
                  className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  style={{
                    color: selectedOrganization
                      ? undefined
                      : "hsl(var(--muted-foreground))",
                  }}
                >
                  <span>
                    {organizationOptions?.find(
                      (opt: any) => opt.value === selectedOrganization
                    )?.label ?? "Select Organization"}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </div>
                {showOrganizationOptions && (
                  <div
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="p-1">
                      {organizationOptions?.map((option: any) => (
                        <div
                          key={option.value}
                          className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          onClick={() => {
                            setSelectedOrganization(option.value);
                            setShowOrganizationOptions(false);
                          }}
                        >
                          {selectedOrganization === option.value && (
                            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                              âœ“
                            </span>
                          )}
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Position */}
            <div className="space-y-2">
              <Label>Position</Label>
              <div className="relative" onClick={e => e.stopPropagation()}>
                <div
                  onClick={e => {
                    e.stopPropagation();
                    setShowPositionOptions(!showPositionOptions);
                  }}
                  className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  style={{
                    color: selectedPosition
                      ? undefined
                      : "hsl(var(--muted-foreground))",
                  }}
                >
                  <span>
                    {positionOptions?.find(
                      (opt: any) => opt.value === selectedPosition
                    )?.label ?? "Select Position"}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </div>
                {showPositionOptions && (
                  <div
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="p-1">
                      {positionOptions?.map((option: any) => (
                        <div
                          key={option.value}
                          className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          onClick={() => {
                            setSelectedPosition(option.value);
                            setShowPositionOptions(false);
                          }}
                        >
                          {selectedPosition === option.value && (
                            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                              âœ“
                            </span>
                          )}
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Department */}
            <div className="space-y-2">
              <Label>Department</Label>
              <div className="relative" onClick={e => e.stopPropagation()}>
                <div
                  onClick={e => {
                    e.stopPropagation();
                    setShowDepartmentOptions(!showDepartmentOptions);
                  }}
                  className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  style={{
                    color: selectedDepartment
                      ? undefined
                      : "hsl(var(--muted-foreground))",
                  }}
                >
                  <span>
                    {departmentOptions?.find(
                      (opt: any) => opt.value === selectedDepartment
                    )?.label ?? "Select Department"}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </div>
                {showDepartmentOptions && (
                  <div
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="p-1">
                      {departmentOptions?.map((option: any) => (
                        <div
                          key={option.value}
                          className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          onClick={() => {
                            setSelectedDepartment(option.value);
                            setShowDepartmentOptions(false);
                          }}
                        >
                          {selectedDepartment === option.value && (
                            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                              âœ“
                            </span>
                          )}
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Banner Image */}
            <div className="space-y-2">
              <Label>Banner Image (Optional)</Label>
              <p className="mb-2 text-xs text-muted-foreground">
                Attach a banner image (JPG, PNG, WEBP) up to 10MB
              </p>
              <CustomImageUpload
                shape="square"
                borderStyle="dashed"
                boxWidth={300}
                boxHeight={150}
                accept="image/jpeg,image/png,image/webp,image/gif"
                value={bannerImage}
                onChange={value => {
                  if (value && !Array.isArray(value)) {
                    setBannerImage(value);
                  } else if (
                    value &&
                    Array.isArray(value) &&
                    value.length > 0
                  ) {
                    setBannerImage(value[0]);
                  } else {
                    setBannerImage(null);
                  }
                }}
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              className="w-full"
              disabled={!isFormValid || isSubmitting || isUploading}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isUploading ? "Uploading Files..." : "Creating Post..."}
                </>
              ) : (
                "Create Post"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
